/**
 * Identify the deployed web bundle from the one artifact Vite already emits.
 *
 *   dist/index.html ──┬─► <script src="/assets/index-A1b2C3.js">
 *                     ├─► <link   href="/assets/index-D4e5F6.css">
 *                     └─► <link   href="/assets/vendor-G7h8I9.js">
 *                              │
 *                              └─► sorted · deduped · joined  =  SIGNATURE
 *
 * Content-hashed filenames change when, and ONLY when, the bundle's bytes
 * change — so two signatures differ exactly when the served JS differs from
 * the running JS. No build-time artifact, no server route, no env var.
 */

/** Asset extensions whose hashed filenames define the bundle. */
const BUNDLE_ASSET_EXTENSIONS = ['.js', '.mjs', '.css'];

/** `src="..."` / `href="..."` on any tag, single or double quoted. */
const ASSET_REFERENCE_PATTERN = /\b(?:src|href)\s*=\s*["']([^"']+)["']/gi;

/** Default poll cadence. One conditional HTML GET per visible tab per interval. */
export const BUILD_SIGNATURE_POLL_INTERVAL_MS = 15 * 60 * 1000;

/** Floor between visibility-triggered checks, so tab-flipping cannot spam the CDN. */
export const BUILD_SIGNATURE_MIN_CHECK_INTERVAL_MS = 60 * 1000;

/** The SPA catch-all rewrite serves this for every path; asking for it by name skips a redirect. */
const INDEX_HTML_PATH = '/index.html';

const isBundleAsset = (reference: string): boolean =>
  reference.startsWith('/') && BUNDLE_ASSET_EXTENSIONS.some((extension) => reference.endsWith(extension));

export function extractBuildSignature(html: string): string | null {
  const assetReferences = new Set<string>();

  for (const match of html.matchAll(ASSET_REFERENCE_PATTERN)) {
    const reference = match[1];
    if (isBundleAsset(reference)) assetReferences.add(reference);
  }

  if (assetReferences.size === 0) return null;

  return [...assetReferences].sort().join('|');
}

export type ReadServedBuildSignature = () => Promise<string | null>;

/**
 * Fetch the currently-served index.html and reduce it to a signature.
 * Any failure resolves to `null`: a flaky poll must never be read as a deploy.
 */
export function createServedBuildSignatureReader({
  fetchImpl = globalThis.fetch,
  indexPath = INDEX_HTML_PATH,
  now = () => Date.now(),
}: {
  fetchImpl?: typeof globalThis.fetch;
  indexPath?: string;
  now?: () => number;
} = {}): ReadServedBuildSignature {
  return async () => {
    try {
      // `cache: no-store` covers the browser cache; the cache-buster covers
      // intermediate proxies that honour neither it nor the origin's headers.
      const response = await fetchImpl(`${indexPath}?buildCheck=${now()}`, {
        cache: 'no-store',
        credentials: 'omit',
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) return null;

      return extractBuildSignature(await response.text());
    } catch {
      return null;
    }
  };
}
